import { roundFte } from "src/util/roundingStrategy";

/**
 * One admin rule is a single `all` or `any` of v1 leaves.
 * A nested group, a deeper tree, or the pause rule stays out of the form.
 */

export const PAUSE_RULE_NAME = "Pause all hiring";

/**
 * sha256 of JSON.stringify({ version: 1, rules: [] }).
 * The API uses that hash when the workspace has no guardrail file yet.
 */
export const EMPTY_GUARDRAILS_CONTENT_HASH =
  "6be6bac38f2d35217ca3cd98e36322f9e8fb6638564f5a87ff660589f6302103";

export const OPERATORS = [
  "equals",
  "not_equals",
  "contains",
  "not_contains",
  "in",
  "not_in",
  "less_than",
  "less_than_or_equal",
  "greater_than",
  "greater_than_or_equal",
  "exists",
  "not_exists",
] as const;

export type Operator = (typeof OPERATORS)[number];
export type RuleGroup = "all" | "any";
export type FieldType = "number" | "string" | "string[]";
export type Effect =
  | "BLOCK"
  | "WARN"
  | "ADD_APPROVAL_STEP"
  | "SKIP_APPROVAL_STEP"
  | "AUTO_APPROVE";
export type RuleStatus = "ACTIVE" | "PAUSED" | "DRAFT";
export type Category = "COST" | "LOCATION" | "STRUCTURE" | "STRATEGIC";
export type Scope = "WORKSPACE" | "SUPPLY_TEAMS" | "DEMAND_TEAMS";

export const isTeamScope = (scope: Scope): boolean =>
  scope === "SUPPLY_TEAMS" || scope === "DEMAND_TEAMS";

export const scopeLabel = (scope: unknown): string => {
  switch (scope) {
    case "SUPPLY_TEAMS":
      return "Supply teams";
    case "DEMAND_TEAMS":
      return "Demand teams";
    case "WORKSPACE":
    default:
      return "Enterprise";
  }
};
export type TemplateId =
  | "outside-plan"
  | "country"
  | "band"
  | "strategic-priority";

export const CORE_FIELDS: Record<string, FieldType> = {
  "requisition.fte": "number",
  "requisition.location": "string",
  "requisition.role": "string",
  "requisition.justification": "string",
  "requisition.hiringManagerId": "string",
  "requisition.targetStartDate": "string",
  "position.establishedFte": "number",
  "position.country": "string",
  "position.city": "string",
  "position.state": "string",
  "position.bands": "string[]",
  "supplyTeam.filledFte": "number",
  "supplyTeam.requestedFte": "number",
  "supplyTeam.effectiveDemandFte": "number",
};

export type LeafDraft = {
  subject: "field" | "sum";
  field: string;
  sumText: string;
  operator: Operator;
  comparison: "value" | "compareToField";
  valueText: string;
  compareToField: string;
};

export type RuleDraft = {
  id?: string;
  name: string;
  description: string;
  category: "" | Category;
  ownerRole: string;
  /** Null when the stored effect is retired. Save stays blocked until this is a current effect. */
  effect: Effect | null;
  /** The stored effect word, when it is retired. */
  retiredEffect?: "ROUTE";
  status: RuleStatus;
  scope: Scope;
  teamIdsText: string;
  /** Set when the effect adds or removes one shared approver group. */
  approverGroupId?: string;
  group: RuleGroup;
  leaves: LeafDraft[];
  /** Template save. A later edit of the stored rule does not set this. */
  forceDraft?: boolean;
};

export type LeafWrite =
  | { field: string; operator: "exists" | "not_exists" }
  | {
      field: string;
      operator: Exclude<Operator, "exists" | "not_exists">;
      value: string | number | string[] | number[];
    }
  | {
      field: string;
      operator: Exclude<Operator, "exists" | "not_exists">;
      compareToField: string;
    }
  | {
      sum: string[];
      operator: Exclude<Operator, "exists" | "not_exists">;
      value: number | number[];
    }
  | {
      sum: string[];
      operator: Exclude<Operator, "exists" | "not_exists">;
      compareToField: string;
    };

type RuleWriteFields = {
  id?: string;
  name: string;
  scope: Scope;
  teamIds?: string[];
  effect: Effect;
  status: RuleStatus;
  description?: string;
  category?: Category;
  ownerRole?: string;
  approverGroupId?: string;
};

export type RuleWrite =
  | (RuleWriteFields & { all: LeafWrite[] })
  | (RuleWriteFields & { any: LeafWrite[] });

export type ApproverGroup = {
  id: string;
  name: string;
  memberPersonIds: string[];
  teamIds: string[];
  slaHours?: number | null;
};

export type ChainTemplateId = "standard" | "exception" | "fast-track";

export type AttributeDefinition = {
  id: string;
  label?: string | null;
  isDeleted?: boolean | null;
};

export type JsonObject = {
  id?: unknown;
  name?: unknown;
  scope?: unknown;
  teamIds?: unknown;
  effect?: unknown;
  status?: unknown;
  description?: unknown;
  category?: unknown;
  ownerRole?: unknown;
  contentHash?: unknown;
  rules?: unknown;
  all?: unknown;
  any?: unknown;
  field?: unknown;
  sum?: unknown;
  operator?: unknown;
  value?: unknown;
  compareToField?: unknown;
  false?: unknown;
  approverGroupId?: unknown;
  approverGroupIds?: unknown;
  approverGroups?: unknown;
  approvalChains?: unknown;
  standard?: unknown;
  exception?: unknown;
  "fast-track"?: unknown;
  memberPersonIds?: unknown;
  slaHours?: unknown;
  refusalReasons?: unknown;
  label?: unknown;
  commentRequired?: unknown;
};

export type RefusalReason = {
  id: string;
  label: string;
  commentRequired?: boolean;
};

const isRecord = (value: unknown): value is JsonObject =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isOperator = (value: unknown): value is Operator =>
  typeof value === "string" && (OPERATORS as readonly string[]).includes(value);

export const isEffect = (value: unknown): value is Effect =>
  value === "BLOCK" ||
  value === "WARN" ||
  value === "ADD_APPROVAL_STEP" ||
  value === "SKIP_APPROVAL_STEP" ||
  value === "AUTO_APPROVE";

/**
 * A current effect, or null when the stored word is the retired ROUTE.
 * A missing or novel value stays the v1 default BLOCK.
 */
export const effectOf = (value: unknown): Effect | null => {
  if (isEffect(value)) {
    return value;
  }
  if (value === "ROUTE") {
    return null;
  }
  return "BLOCK";
};

export const isMatchEffect = (effect: Effect | null): boolean =>
  effect === "ADD_APPROVAL_STEP" ||
  effect === "SKIP_APPROVAL_STEP" ||
  effect === "AUTO_APPROVE";

export const EFFECT_LABEL: Record<Effect, string> = {
  BLOCK: "Blocking",
  WARN: "Warning",
  ADD_APPROVAL_STEP: "Add a group",
  SKIP_APPROVAL_STEP: "Remove a group",
  AUTO_APPROVE: "Use the fast-track template",
};

const existsOperator = (
  operator: Operator
): operator is "exists" | "not_exists" =>
  operator === "exists" || operator === "not_exists";

const substringOperator = (
  operator: Operator
): operator is "contains" | "not_contains" =>
  operator === "contains" || operator === "not_contains";

const isPlainValue = (value: unknown): boolean => {
  if (typeof value === "string" || typeof value === "number") {
    return true;
  }
  return (
    Array.isArray(value) &&
    value.every((item) => typeof item === "string" || typeof item === "number")
  );
};

const isLeaf = (node: unknown): boolean => {
  if (!isRecord(node) || "all" in node || "any" in node) {
    return false;
  }
  const hasField = "field" in node;
  const hasSum = "sum" in node;
  if (hasField === hasSum || !isOperator(node.operator)) {
    return false;
  }
  if (hasField && (typeof node.field !== "string" || !node.field.trim())) {
    return false;
  }
  if (hasSum) {
    if (
      !Array.isArray(node.sum) ||
      node.sum.length === 0 ||
      node.sum.some((field) => typeof field !== "string" || !field.trim())
    ) {
      return false;
    }
  }
  if (existsOperator(node.operator)) {
    return hasField && !("value" in node) && !("compareToField" in node);
  }
  const hasValue = "value" in node;
  const hasCompare = "compareToField" in node;
  if (hasValue === hasCompare) {
    return false;
  }
  if (hasCompare) {
    return (
      typeof node.compareToField === "string" &&
      node.compareToField.trim() !== ""
    );
  }
  return isPlainValue(node.value);
};

const groupChildren = (
  rule: JsonObject
): { group: RuleGroup; children: unknown[] } | null => {
  const hasAll = "all" in rule;
  const hasAny = "any" in rule;
  if (hasAll === hasAny) {
    return null;
  }
  const group: RuleGroup = hasAll ? "all" : "any";
  const children = hasAll ? rule.all : rule.any;
  if (!Array.isArray(children)) {
    return null;
  }
  return { group, children };
};

/** Pause, nested groups, and anything that is not one flat `all` or `any`. */
export const isFormRule = (rule: unknown): boolean => {
  if (!isRecord(rule) || rule["false"] === true) {
    return false;
  }
  const grouped = groupChildren(rule);
  return (
    grouped !== null &&
    grouped.children.length > 0 &&
    grouped.children.every(isLeaf)
  );
};

export const isPauseRule = (rule: {
  name?: unknown;
  scope?: unknown;
}): boolean => rule.name === PAUSE_RULE_NAME && rule.scope === "WORKSPACE";

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.flatMap((item) =>
        typeof item === "string" && item.trim() !== "" ? [item.trim()] : []
      )
    : [];

export const approverGroupsOf = (document: unknown): ApproverGroup[] => {
  if (!isRecord(document) || !Array.isArray(document.approverGroups)) {
    return [];
  }
  return document.approverGroups.flatMap((item) => {
    if (
      !isRecord(item) ||
      typeof item.id !== "string" ||
      typeof item.name !== "string"
    ) {
      return [];
    }
    return [
      {
        id: item.id,
        name: item.name,
        memberPersonIds: stringList(item.memberPersonIds),
        teamIds: stringList(item.teamIds),
        slaHours:
          typeof item.slaHours === "number" && Number.isFinite(item.slaHours)
            ? item.slaHours
            : null,
      },
    ];
  });
};

/** Workspace list. A blank id or label is dropped. commentRequired is kept only when true. */
export const refusalReasonsOf = (document: unknown): RefusalReason[] => {
  if (!isRecord(document) || !Array.isArray(document.refusalReasons)) {
    return [];
  }
  return document.refusalReasons.flatMap((item) => {
    if (
      !isRecord(item) ||
      typeof item.id !== "string" ||
      typeof item.label !== "string"
    ) {
      return [];
    }
    const id = item.id.trim();
    const label = item.label.trim();
    if (id === "" || label === "") {
      return [];
    }
    return [
      {
        id,
        label,
        ...(item.commentRequired === true ? { commentRequired: true } : {}),
      },
    ];
  });
};

export type ApprovalChainStep = {
  approverGroupIds: string[];
};

const templateStepGroupIds = (step: JsonObject): string[] => {
  const ids = stringList(step.approverGroupIds);
  if (ids.length > 0) {
    return ids;
  }
  const legacy =
    typeof step.approverGroupId === "string" ? step.approverGroupId.trim() : "";
  return legacy === "" ? [] : [legacy];
};

const stepsOf = (value: unknown): ApprovalChainStep[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((step) => {
    if (!isRecord(step)) {
      return [];
    }
    const approverGroupIds = templateStepGroupIds(step);
    return approverGroupIds.length > 0 ? [{ approverGroupIds }] : [];
  });
};

/** Ordered steps for the three templates. A missing template is an empty list. */
export const approvalChainsOf = (
  document: unknown
): Record<ChainTemplateId, ApprovalChainStep[]> => {
  const chains =
    isRecord(document) && isRecord(document.approvalChains)
      ? document.approvalChains
      : null;
  return {
    standard: stepsOf(chains?.standard),
    exception: stepsOf(chains?.exception),
    "fast-track": stepsOf(chains?.["fast-track"]),
  };
};

/**
 * Workspace BLOCK rule. No `all` and no `any`. The form does not edit it.
 * `"false": true` is the requirement that never holds, so the block fires.
 */
export const pauseRuleBody = (id?: string): JsonObject => ({
  ...(id ? { id } : {}),
  name: PAUSE_RULE_NAME,
  scope: "WORKSPACE",
  effect: "BLOCK",
  status: "ACTIVE",
  ["false"]: true,
});

export const findPauseRule = (rules: JsonObject[]): JsonObject | undefined =>
  rules.find((rule) => isPauseRule(rule));

export const contentHashOf = (document: unknown): string | null => {
  if (document == null) {
    return EMPTY_GUARDRAILS_CONTENT_HASH;
  }
  if (!isRecord(document)) {
    return null;
  }
  const hash = document.contentHash;
  if (typeof hash === "string" && hash.trim()) {
    return hash.trim();
  }
  if (Array.isArray(document.rules) && document.rules.length === 0) {
    return EMPTY_GUARDRAILS_CONTENT_HASH;
  }
  return null;
};

export const rulesOf = (document: unknown): JsonObject[] => {
  if (!isRecord(document) || !Array.isArray(document.rules)) {
    return [];
  }
  return document.rules.filter(isRecord);
};

const scopeRank = (scope: unknown): number => {
  switch (scope) {
    case "WORKSPACE":
      return 0;
    case "SUPPLY_TEAMS":
      return 1;
    case "DEMAND_TEAMS":
      return 2;
    default:
      return 3;
  }
};

/** Enterprise, then supply teams, then demand teams. Order inside a scope stays as stored. */
export const orderRules = <T extends { scope?: unknown }>(rules: T[]): T[] =>
  rules
    .map((rule, index) => ({ rule, index }))
    .sort((left, right) => {
      const byScope = scopeRank(left.rule.scope) - scopeRank(right.rule.scope);
      return byScope !== 0 ? byScope : left.index - right.index;
    })
    .map(({ rule }) => rule);

export const categoryLabel = (category: unknown): string => {
  switch (category) {
    case "COST":
      return "Cost and envelope";
    case "LOCATION":
      return "Location and compliance";
    case "STRUCTURE":
      return "Structure and shape";
    case "STRATEGIC":
      return "Strategic alignment";
    default:
      return "Uncategorised";
  }
};

export const statusLabel = (status: unknown): string => {
  switch (status) {
    case "PAUSED":
      return "Paused";
    case "DRAFT":
      return "Draft";
    case "ACTIVE":
    case null:
    case undefined:
      return "Active";
    default:
      return typeof status === "string" && status.trim()
        ? status.trim()
        : "Active";
  }
};

export const catalogTypes = (
  definitions: AttributeDefinition[]
): Record<string, FieldType> => {
  const types: Record<string, FieldType> = { ...CORE_FIELDS };
  for (const definition of definitions) {
    const id = definition.id.trim();
    if (definition.isDeleted === true || !id) {
      continue;
    }
    types[`requisition.extraAttributes.${id}`] = "string";
  }
  return types;
};

export const strategicAttributeId = (
  definitions: AttributeDefinition[]
): string | null => {
  const match = definitions.find(
    (definition) =>
      definition.isDeleted !== true &&
      /strategic/i.test(`${definition.id} ${definition.label ?? ""}`)
  );
  const id = match?.id.trim();
  return id ? id : null;
};

export const operatorsFor = (
  type: FieldType | null,
  subject: "field" | "sum"
): Operator[] => {
  if (subject === "sum" || type === "number") {
    return OPERATORS.filter(
      (operator) => !existsOperator(operator) && !substringOperator(operator)
    );
  }
  if (type === "string[]") {
    return ["in", "not_in"];
  }
  return [...OPERATORS];
};

export const OPERATOR_LABEL: Record<Operator, string> = {
  equals: "equals",
  not_equals: "not equals",
  contains: "contains",
  not_contains: "does not contain",
  in: "in",
  not_in: "not in",
  less_than: "less than",
  less_than_or_equal: "less than or equal",
  greater_than: "greater than",
  greater_than_or_equal: "greater than or equal",
  exists: "exists",
  not_exists: "not exists",
};

const blankLeaf = (): LeafDraft => ({
  subject: "field",
  field: "requisition.fte",
  sumText: "",
  operator: "less_than_or_equal",
  comparison: "value",
  valueText: "",
  compareToField: "",
});

export const blankRule = (): RuleDraft => ({
  name: "",
  description: "",
  category: "",
  ownerRole: "",
  effect: "BLOCK",
  status: "ACTIVE",
  scope: "WORKSPACE",
  teamIdsText: "",
  group: "all",
  leaves: [blankLeaf()],
});

const leaf = (patch: Partial<LeafDraft>): LeafDraft => ({
  ...blankLeaf(),
  ...patch,
});

export const TEMPLATES: {
  id: TemplateId;
  name: string;
  description: string;
}[] = [
  {
    id: "outside-plan",
    name: "Outside plan",
    description:
      "Filled FTE plus requested FTE stays within the supply-team plan.",
  },
  {
    id: "country",
    name: "Country",
    description: "The position country is one of the listed countries.",
  },
  {
    id: "band",
    name: "Band",
    description: "The position band is one of the listed bands.",
  },
  {
    id: "strategic-priority",
    name: "Strategic priority",
    description:
      "Saves as a draft until a strategic priority attribute exists.",
  },
];

/** Copied onto one requisition. Not a second workflow editor. */
export const CHAIN_TEMPLATES: {
  id: ChainTemplateId;
  description: string;
}[] = [
  {
    id: "standard",
    description:
      "No failed check, and no AUTO_APPROVE match. The requisition stores this generated list.",
  },
  {
    id: "exception",
    description:
      "A WARN failure, and no AUTO_APPROVE match. The list starts with the requestor's reason, after the automatic evaluation step. The check stays FAIL.",
  },
  {
    id: "fast-track",
    description:
      "An AUTO_APPROVE rule matches. HARD_BLOCK still wins and writes no list.",
  },
];

export const templateDraft = (
  id: TemplateId,
  strategicId: string | null
): RuleDraft => {
  switch (id) {
    case "outside-plan":
      return {
        name: "Outside plan",
        description:
          "Filled FTE plus requested FTE stays within the supply-team plan.",
        category: "COST",
        ownerRole: "",
        effect: "BLOCK",
        status: "ACTIVE",
        scope: "SUPPLY_TEAMS",
        teamIdsText: "",
        group: "all",
        leaves: [
          leaf({
            subject: "sum",
            field: "",
            sumText: "supplyTeam.filledFte, supplyTeam.requestedFte",
            operator: "less_than_or_equal",
            comparison: "compareToField",
            compareToField: "supplyTeam.effectiveDemandFte",
          }),
        ],
      };
    case "country":
      return {
        name: "Country",
        description: "The position country is one of the listed countries.",
        category: "LOCATION",
        ownerRole: "",
        effect: "BLOCK",
        status: "ACTIVE",
        scope: "WORKSPACE",
        teamIdsText: "",
        group: "all",
        leaves: [
          leaf({
            field: "position.country",
            operator: "in",
            valueText: "",
          }),
        ],
      };
    case "band":
      return {
        name: "Band",
        description: "The position band is one of the listed bands.",
        category: "STRUCTURE",
        ownerRole: "",
        effect: "BLOCK",
        status: "ACTIVE",
        scope: "WORKSPACE",
        teamIdsText: "",
        group: "all",
        leaves: [
          leaf({
            field: "position.bands",
            operator: "in",
            valueText: "",
          }),
        ],
      };
    case "strategic-priority":
      return {
        name: "Strategic priority",
        description:
          "Stays a draft until a strategic priority attribute exists.",
        category: "STRATEGIC",
        ownerRole: "",
        effect: "BLOCK",
        status: "DRAFT",
        forceDraft: true,
        scope: "WORKSPACE",
        teamIdsText: "",
        group: "all",
        leaves: [
          leaf({
            field: strategicId
              ? `requisition.extraAttributes.${strategicId}`
              : "",
            operator: "exists",
          }),
        ],
      };
    default: {
      const unexpected: never = id;
      return unexpected;
    }
  }
};

const valueTextOf = (value: unknown): string => {
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "string") {
    return value;
  }
  if (!Array.isArray(value)) {
    return "";
  }
  return value
    .flatMap((item) =>
      typeof item === "number"
        ? [String(item)]
        : typeof item === "string"
          ? [item]
          : []
    )
    .join(", ");
};

export const draftFromRule = (rule: unknown): RuleDraft | null => {
  if (!isFormRule(rule) || !isRecord(rule)) {
    return null;
  }
  const grouped = groupChildren(rule);
  if (!grouped) {
    return null;
  }
  const scope =
    rule.scope === "SUPPLY_TEAMS" || rule.scope === "DEMAND_TEAMS"
      ? rule.scope
      : "WORKSPACE";
  const status =
    rule.status === "PAUSED" || rule.status === "DRAFT"
      ? rule.status
      : "ACTIVE";
  const effect = effectOf(rule.effect);
  const category =
    rule.category === "COST" ||
    rule.category === "LOCATION" ||
    rule.category === "STRUCTURE" ||
    rule.category === "STRATEGIC"
      ? rule.category
      : "";
  const id = typeof rule.id === "string" ? rule.id : "";
  return {
    ...(id ? { id } : {}),
    name: typeof rule.name === "string" ? rule.name : "",
    description: typeof rule.description === "string" ? rule.description : "",
    category,
    ownerRole: typeof rule.ownerRole === "string" ? rule.ownerRole : "",
    effect,
    ...(effect === null ? { retiredEffect: "ROUTE" as const } : {}),
    status,
    scope,
    approverGroupId:
      typeof rule.approverGroupId === "string" ? rule.approverGroupId : "",
    teamIdsText: Array.isArray(rule.teamIds)
      ? rule.teamIds.filter((id) => typeof id === "string").join(", ")
      : "",
    group: grouped.group,
    leaves: grouped.children.filter(isRecord).map((node) => {
      const hasSum = Array.isArray(node.sum);
      const operator = isOperator(node.operator) ? node.operator : "equals";
      const comparison =
        typeof node.compareToField === "string" ? "compareToField" : "value";
      return leaf({
        subject: hasSum ? "sum" : "field",
        field: typeof node.field === "string" ? node.field : "",
        sumText: hasSum ? (node.sum as string[]).join(", ") : "",
        operator,
        comparison,
        valueText: "value" in node ? valueTextOf(node.value) : "",
        compareToField:
          typeof node.compareToField === "string" ? node.compareToField : "",
      });
    }),
  };
};

export const splitList = (text: string): string[] =>
  text
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");

export const compareFieldPaths = (
  draft: LeafDraft,
  types: Record<string, FieldType>
): string[] => {
  const type = draft.subject === "sum" ? "number" : types[draft.field];
  if (!type) {
    return [];
  }
  return Object.entries(types)
    .filter(([, candidate]) => compatible(draft.operator, type, candidate))
    .map(([path]) => path);
};

const compatible = (
  operator: Operator,
  fieldType: FieldType,
  compareType: FieldType
): boolean => {
  if (substringOperator(operator)) {
    return false;
  }
  if (operator === "in" || operator === "not_in") {
    return (
      compareType === "string[]" &&
      (fieldType === "string" || fieldType === "string[]")
    );
  }
  return compareType === fieldType && fieldType !== "string[]";
};

const parseValue = (
  text: string,
  type: FieldType,
  operator: Operator
):
  | { ok: true; value: string | number | string[] | number[] }
  | { ok: false; error: string } => {
  if (operator === "in" || operator === "not_in") {
    const parts = splitList(text);
    if (parts.length === 0) {
      return { ok: false, error: "Enter at least one value." };
    }
    if (type === "number") {
      const numbers = parts.map((part) => Number(part));
      if (numbers.some((number) => !Number.isFinite(number))) {
        return { ok: false, error: "Values must be numbers." };
      }
      return { ok: true, value: numbers };
    }
    return { ok: true, value: parts };
  }
  if (type === "number") {
    if (text.trim() === "" || !Number.isFinite(Number(text))) {
      return { ok: false, error: "Enter a number." };
    }
    return { ok: true, value: Number(text) };
  }
  if (type === "string[]") {
    return {
      ok: false,
      error: "A list field uses in or not in.",
    };
  }
  if (text.trim() === "") {
    return { ok: false, error: "Enter a value." };
  }
  return { ok: true, value: text };
};

const writeLeaf = (
  draft: LeafDraft,
  types: Record<string, FieldType>,
  index: number
): { ok: true; leaf: LeafWrite } | { ok: false; error: string } => {
  const path = `Condition ${index + 1}`;
  const subjectType = (): FieldType | null => {
    if (draft.subject === "sum") {
      return "number";
    }
    const type = types[draft.field];
    return type ?? null;
  };
  if (draft.subject === "field" && !types[draft.field]) {
    return {
      ok: false,
      error: draft.field
        ? `${path}: ${draft.field} is not an allowed field.`
        : `${path}: choose a field.`,
    };
  }
  const sum = splitList(draft.sumText);
  if (draft.subject === "sum") {
    if (sum.length === 0) {
      return { ok: false, error: `${path}: enter at least one sum field.` };
    }
    const invalid = sum.find((field) => types[field] !== "number");
    if (invalid) {
      return {
        ok: false,
        error: `${path}: ${invalid} is not a number field.`,
      };
    }
  }
  const type = subjectType();
  if (!type) {
    return { ok: false, error: `${path}: choose a field.` };
  }
  const allowed = operatorsFor(type, draft.subject);
  if (!allowed.includes(draft.operator)) {
    return { ok: false, error: `${path}: that operator is not allowed here.` };
  }
  const subject = draft.subject === "sum" ? { sum } : { field: draft.field };
  if (existsOperator(draft.operator)) {
    if (draft.subject === "sum") {
      return { ok: false, error: `${path}: exists is not allowed on a sum.` };
    }
    return {
      ok: true,
      leaf: { field: draft.field, operator: draft.operator },
    };
  }
  if (draft.comparison === "compareToField") {
    const compareType = types[draft.compareToField];
    if (!compareType || !compatible(draft.operator, type, compareType)) {
      return {
        ok: false,
        error: `${path}: choose a compatible field to compare.`,
      };
    }
    return {
      ok: true,
      leaf: {
        ...subject,
        operator: draft.operator,
        compareToField: draft.compareToField,
      },
    };
  }
  const parsed = parseValue(draft.valueText, type, draft.operator);
  if (!parsed.ok) {
    return { ok: false, error: `${path}: ${parsed.error}` };
  }
  if (draft.subject === "sum") {
    if (typeof parsed.value === "number") {
      return {
        ok: true,
        leaf: { sum, operator: draft.operator, value: parsed.value },
      };
    }
    if (
      Array.isArray(parsed.value) &&
      parsed.value.every((item): item is number => typeof item === "number")
    ) {
      return {
        ok: true,
        leaf: { sum, operator: draft.operator, value: parsed.value },
      };
    }
    return { ok: false, error: `${path}: enter a number.` };
  }
  return {
    ok: true,
    leaf: {
      field: draft.field,
      operator: draft.operator,
      value: parsed.value,
    },
  };
};

export const toRule = (
  draft: RuleDraft,
  types: Record<string, FieldType>
): { ok: true; rule: RuleWrite } | { ok: false; error: string } => {
  const name = draft.name.trim();
  if (!name) {
    return { ok: false, error: "Enter a name." };
  }
  if (name === PAUSE_RULE_NAME && draft.scope === "WORKSPACE") {
    return { ok: false, error: "Use the pause switch for Pause all hiring." };
  }
  if (!isEffect(draft.effect)) {
    return {
      ok: false,
      error: "Choose a current effect. The stored effect is retired.",
    };
  }
  if (draft.leaves.length === 0) {
    return { ok: false, error: "Add at least one condition." };
  }
  const leaves: LeafWrite[] = [];
  for (const [index, item] of draft.leaves.entries()) {
    const written = writeLeaf(item, types, index);
    if (!written.ok) {
      return written;
    }
    leaves.push(written.leaf);
  }
  const teamIds = splitList(draft.teamIdsText);
  const teamScoped = isTeamScope(draft.scope);
  if (teamScoped && teamIds.length === 0) {
    return {
      ok: false,
      error:
        draft.scope === "DEMAND_TEAMS"
          ? "Choose at least one demand team."
          : "Choose at least one supply team.",
    };
  }
  if (
    teamScoped &&
    (draft.effect === "SKIP_APPROVAL_STEP" || draft.effect === "AUTO_APPROVE")
  ) {
    return {
      ok: false,
      error:
        "A team-scoped rule cannot remove a group or use the fast-track template.",
    };
  }
  const approverGroupId = draft.approverGroupId?.trim() ?? "";
  if (
    (draft.effect === "ADD_APPROVAL_STEP" ||
      draft.effect === "SKIP_APPROVAL_STEP") &&
    !approverGroupId
  ) {
    return { ok: false, error: "Choose an approver group." };
  }
  const description = draft.description.trim();
  const ownerRole = draft.ownerRole.trim();
  const fields: RuleWriteFields = {
    ...(draft.id ? { id: draft.id } : {}),
    name,
    scope: draft.scope,
    ...(teamScoped ? { teamIds } : {}),
    effect: draft.effect,
    status: draft.forceDraft ? "DRAFT" : draft.status,
    ...(draft.effect === "ADD_APPROVAL_STEP" ||
    draft.effect === "SKIP_APPROVAL_STEP"
      ? { approverGroupId }
      : {}),
    ...(description ? { description } : {}),
    ...(draft.category ? { category: draft.category } : {}),
    ...(ownerRole ? { ownerRole } : {}),
  };
  switch (draft.group) {
    case "all":
      return { ok: true, rule: { ...fields, all: leaves } };
    case "any":
      return { ok: true, rule: { ...fields, any: leaves } };
    default: {
      const unexpected: never = draft.group;
      return unexpected;
    }
  }
};

export const sumFte = (rows: Array<{ fte?: number | null }>): number =>
  rows.reduce(
    (total, row) => total + (typeof row.fte === "number" ? row.fte : 0),
    0
  );

export const blastLine = ({
  pending,
  inFlightCount,
  fte,
  decimal,
  truncated,
}: {
  pending: boolean;
  inFlightCount: number | null;
  fte: number | null;
  decimal: number;
  truncated: { loaded: number; total: number } | null;
}): string => {
  if (pending) {
    return "Loading the in-flight count.";
  }
  const count =
    inFlightCount == null
      ? "In-flight count is not available"
      : `${inFlightCount} hiring requests in flight`;
  const fteText = fte == null ? "" : `, ${roundFte(fte, decimal)} FTE`;
  const tail = truncated
    ? ` FTE sum uses the first ${truncated.loaded} of ${truncated.total} requests.`
    : "";
  return `${count}${fteText}. Cost: —.${tail}`;
};
